import jwtDecode from 'jwt-decode';
import url from '../../constants/urlList';
import { isTokenPersistable } from '../../utils/session';
import { parseChallenge } from '../institutional/institutional-api';

// The existing fetch interceptor supplies the CURRENT account's bearer token.
// Secondary Google proof is a request body only; it never becomes the session.
const responseErrorCode = (response, data) => {
  if (typeof data?.code === 'string' && /^[A-Z][A-Z0-9_]{1,63}$/.test(data.code)) return data.code;
  if (response.status === 401) return 'REAUTHENTICATION_REQUIRED';
  return 'REQUEST_FAILED';
};
const request = async (path, body) => {
  try {
    const response = await fetch(`${url.domain}/qlearn/account/connections${path}`, {
      method: body === undefined ? 'GET' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    if (!response) return { ok: false, code: 'REAUTHENTICATION_REQUIRED' };
    const data = await response.json().catch(() => null);
    if (response.status >= 200 && response.status < 300) return { ok: true, data };
    return { ok: false, code: responseErrorCode(response, data) };
  } catch (_) {
    // A transport exception has no authoritative connection outcome. Keep the
    // intent retryable and never expose exception text that could contain proof.
    return { ok: false, code: 'NETWORK_ERROR' };
  }
};
export const getConnections = () => request('');
export const sendInstitutionalCode = async (email) => {
  const result = await request('/institutional/send-otp', { email });
  if (!result.ok) return result;
  const challenge = parseChallenge(result.data);
  return challenge ? { ...result, challenge } : { ok: false, code: 'BAD_RESPONSE' };
};
export const verifyInstitutionalCode = ({ email, challengeId, otp }) =>
  request('/institutional/verify-otp', { email, challengeId, otp });
export const previewGoogle = (googleSessionToken) => request('/google/preview', { googleSessionToken });
export const confirmConnection = (intentId) => request('/confirm', { intentId });

// Structural validation only; the server is authoritative for signature/proof.
// This blocks malformed, stale or mismatched session responses from overwriting
// the current account's session after the server has combined its records.
export const isConnectionSession = (session) => {
  if (!session || typeof session.id !== 'string' || !session.id
    || !Array.isArray(session.roles) || session.roles.length === 0
    || !session.roles.every(role => ['ROLE_STUDENT', 'ROLE_TUTOR', 'ROLE_ADMIN'].includes(role))
    || (session.primaryRole != null && !session.roles.includes(session.primaryRole))
    || typeof session.isProfileComplete !== 'boolean'
    || !isTokenPersistable(session.accessToken)) return false;
  try {
    const claims = jwtDecode(session.accessToken);
    return claims.purpose === 'session' && claims.sub === session.id;
  } catch (_) {
    // Undecodable session claims must not replace the currently signed-in
    // account. Deliberately discard the decoder error without logging tokens.
    return false;
  }
};
export const saveConnectionSession = (session, actor) => {
  const storage = window.localStorage;
  if (!isConnectionSession(session) || storage.getItem('token') !== actor.token
    || storage.getItem('userId') !== actor.id) return false;
  // No attribution/referral keys are removed and this is not a new signup.
  ['userdetails', 'role', 'institutionalPendingProfile', 'pendingEmailVerify', 'oauthPending']
    .forEach(key => storage.removeItem(key));
  storage.setItem('userId', session.id);
  storage.setItem('isProfileComplete', String(session.isProfileComplete));
  if (session.primaryRole) storage.setItem('role', session.primaryRole);
  storage.setItem('token', session.accessToken);
  return true;
};
